import { useEffect, useState } from 'react'

import { Input, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useDebounce } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { CustomerSelect } from '@/components/CustomerSelect'
import { useIsAdmin } from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import queries from '../queries'

import { TelePoliciesList } from './TelePoliciesList'
import { TelePoliciesModal } from './TelePoliciesModal'

import './index.styles.scss'

export const TelePoliciesListPage = () => {
  const isAdmin = useIsAdmin()

  const navigate = useNavigate()
  const translate = useTranslation()
  const [telePolicyModal, setTelePolicyModal] = useState<{
    isOpen: boolean
    telePolicyId?: number
  }>({
    isOpen: false,
  })
  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')

  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const {
    data: list = [],
    isLoading,
    isSuccess,
  } = useQuery(queries.adminTelePoliciesList(), adminResources.getTelePolicyList, {
    enabled: isAdmin,
  })

  const details =
    list && telePolicyModal.telePolicyId
      ? list.find(({ id }) => id === telePolicyModal.telePolicyId)
      : null

  return (
    <Container onSwipedLeft={() => navigate(APP_ROUTES.HOME)} className="tele-policies">
      <Container.Header>
        <Translation id="Tele policies" />
      </Container.Header>

      <div className="table-actions">
        <Button
          Icon={<PlusIcon />}
          disabled={!isSuccess}
          className="mb-4 ml-0"
          onClick={() => setTelePolicyModal({ isOpen: true })}
        >
          <Translation id="New Tele Policy" />
        </Button>

        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <Input
            id="searchFilter"
            type="text"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />

          <CustomerSelect
            hasGlobalFilter
            filterData={
              list
                ?.filter(item => item.customer && item.customer.id && item.customer.name)
                .map(item => ({
                  customerId: item.customer!.id,
                  customerName: item.customer!.name,
                })) || []
            }
            isLoading={isLoading}
          />
        </div>
      </div>

      <TelePoliciesList searchFilter={searchFilter} onClick={setTelePolicyModal} />

      <Popup
        variant="narrow"
        ariaLabel={translate('Add or edit tele policy')}
        isOpen={telePolicyModal.isOpen}
        onClose={() => setTelePolicyModal({ isOpen: false })}
      >
        <TelePoliciesModal
          details={details || null}
          list={list}
          onClose={() => setTelePolicyModal({ isOpen: false })}
        />
      </Popup>
    </Container>
  )
}
